// Workflow templates for quick setup

export interface WorkflowTrigger {
  type: 'contact_created' | 'deal_won' | 'deal_lost' | 'contact_updated' | 'activity_completed' | 'lifecycle_stage_changed';
  conditions?: Array<{
    field: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
    value: any;
  }>;
}

export interface WorkflowAction {
  type: 'send_email' | 'create_task' | 'update_field' | 'add_tag' | 'create_activity' | 'send_notification' | 'wait';
  config: {
    [key: string]: any;
  };
  delay?: number; // Delay in minutes before executing
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: 'sales' | 'marketing' | 'onboarding' | 'nurture' | 'support';
  icon: string;
  trigger: WorkflowTrigger;
  actions: WorkflowAction[];
  popular?: boolean;
}

export const workflowTemplates: WorkflowTemplate[] = [
  // Sales Workflows
  {
    id: 'welcome-new-contact',
    name: 'Welcome New Contact',
    description: 'Send welcome email and create follow-up task when a new contact is added',
    category: 'sales',
    icon: '👋',
    popular: true,
    trigger: {
      type: 'contact_created'
    },
    actions: [
      {
        type: 'send_email',
        config: {
          template: 'welcome_email',
          subject: 'Welcome to our community!',
          from: 'sales@company.com'
        }
      },
      {
        type: 'create_task',
        config: {
          title: 'Follow up with new contact',
          description: 'Reach out to introduce yourself and understand their needs',
          dueInDays: 1
        },
        delay: 60 // 1 hour later
      }
    ]
  },
  {
    id: 'deal-won-celebration',
    name: 'Deal Won Celebration',
    description: 'Send celebration email and create onboarding tasks when a deal is won',
    category: 'sales',
    icon: '🎉',
    popular: true,
    trigger: {
      type: 'deal_won'
    },
    actions: [
      {
        type: 'send_email',
        config: {
          template: 'deal_won_celebration',
          subject: 'Welcome aboard! Let\'s get started',
          from: 'onboarding@company.com'
        }
      },
      {
        type: 'create_task',
        config: {
          title: 'Schedule kickoff call',
          description: 'Schedule onboarding kickoff call with the new customer',
          dueInDays: 1
        }
      },
      {
        type: 'add_tag',
        config: {
          tagName: 'Customer'
        }
      },
      {
        type: 'send_notification',
        config: {
          channel: 'slack',
          message: '🎉 New deal won! Time to celebrate and onboard.'
        }
      }
    ]
  },
  {
    id: 'deal-lost-follow-up',
    name: 'Deal Lost Follow-up',
    description: 'Create task to understand why deal was lost and schedule future check-in',
    category: 'sales',
    icon: '📋',
    trigger: {
      type: 'deal_lost'
    },
    actions: [
      {
        type: 'create_task',
        config: {
          title: 'Loss reason analysis',
          description: 'Document why the deal was lost and what we can learn',
          dueInDays: 0
        }
      },
      {
        type: 'wait',
        config: {
          duration: 90,
          unit: 'days'
        }
      },
      {
        type: 'create_task',
        config: {
          title: 'Re-engage lost prospect',
          description: 'Check in to see if circumstances have changed',
          dueInDays: 0
        }
      }
    ]
  },

  // Marketing Workflows
  {
    id: 'lead-nurture-sequence',
    name: 'Lead Nurture Sequence',
    description: 'Automated email sequence to nurture new leads over 30 days',
    category: 'marketing',
    icon: '🌱',
    popular: true,
    trigger: {
      type: 'contact_created',
      conditions: [
        { field: 'lifecycle_stage', operator: 'equals', value: 'lead' }
      ]
    },
    actions: [
      {
        type: 'send_email',
        config: {
          template: 'nurture_day_1',
          subject: 'Resources to get started',
          from: 'marketing@company.com'
        },
        delay: 0
      },
      {
        type: 'send_email',
        config: {
          template: 'nurture_day_7',
          subject: 'Customer success stories',
          from: 'marketing@company.com'
        },
        delay: 10080 // 7 days
      },
      {
        type: 'send_email',
        config: {
          template: 'nurture_day_14',
          subject: 'How we can help you succeed',
          from: 'marketing@company.com'
        },
        delay: 20160 // 14 days
      },
      {
        type: 'create_task',
        config: {
          title: 'Personal outreach to nurtured lead',
          description: 'Lead has been nurtured for 30 days - time for personal touch',
          dueInDays: 30
        },
        delay: 43200 // 30 days
      }
    ]
  },
  {
    id: 'mql-to-sql-transition',
    name: 'MQL to SQL Transition',
    description: 'Notify sales team and create tasks when a lead becomes sales qualified',
    category: 'marketing',
    icon: '🚀',
    trigger: {
      type: 'lifecycle_stage_changed',
      conditions: [
        { field: 'new_stage', operator: 'equals', value: 'sql' }
      ]
    },
    actions: [
      {
        type: 'send_notification',
        config: {
          channel: 'sales_team',
          message: '🔥 New SQL! Time to reach out.'
        }
      },
      {
        type: 'create_task',
        config: {
          title: 'Contact new SQL within 24 hours',
          description: 'High-priority: Reach out to newly qualified lead',
          dueInDays: 0,
          priority: 'high'
        }
      },
      {
        type: 'add_tag',
        config: {
          tagName: 'Hot Lead'
        }
      }
    ]
  },

  // Onboarding Workflows
  {
    id: 'customer-onboarding',
    name: 'Customer Onboarding Journey',
    description: 'Multi-step onboarding process for new customers over 60 days',
    category: 'onboarding',
    icon: '🎓',
    popular: true,
    trigger: {
      type: 'contact_updated',
      conditions: [
        { field: 'lifecycle_stage', operator: 'equals', value: 'customer' }
      ]
    },
    actions: [
      {
        type: 'send_email',
        config: {
          template: 'onboarding_welcome',
          subject: 'Welcome! Here\'s how to get started',
          from: 'success@company.com'
        }
      },
      {
        type: 'create_task',
        config: {
          title: 'Schedule onboarding call',
          description: 'Book 30-min onboarding session',
          dueInDays: 2
        }
      },
      {
        type: 'send_email',
        config: {
          template: 'onboarding_week_2',
          subject: 'Quick wins and best practices',
          from: 'success@company.com'
        },
        delay: 10080 // 7 days
      },
      {
        type: 'send_email',
        config: {
          template: 'onboarding_week_4',
          subject: 'Advanced features you\'ll love',
          from: 'success@company.com'
        },
        delay: 20160 // 14 days
      },
      {
        type: 'create_task',
        config: {
          title: 'Check-in with customer',
          description: '30-day check-in to ensure satisfaction',
          dueInDays: 30
        },
        delay: 43200 // 30 days
      }
    ]
  },

  // Nurture Workflows
  {
    id: 'inactive-contact-reengagement',
    name: 'Re-engage Inactive Contacts',
    description: 'Automated sequence to re-engage contacts with no activity in 90 days',
    category: 'nurture',
    icon: '🔄',
    trigger: {
      type: 'contact_updated',
      conditions: [
        { field: 'days_since_last_activity', operator: 'greater_than', value: 90 }
      ]
    },
    actions: [
      {
        type: 'send_email',
        config: {
          template: 'reengagement_email',
          subject: 'We miss you! Here\'s what\'s new',
          from: 'marketing@company.com'
        }
      },
      {
        type: 'create_task',
        config: {
          title: 'Personal outreach to inactive contact',
          description: 'Call or email to re-establish relationship',
          dueInDays: 7
        },
        delay: 10080 // 7 days if no response
      }
    ]
  },

  // Support Workflows
  {
    id: 'task-completion-follow-up',
    name: 'Task Completion Follow-up',
    description: 'Send follow-up email after completing a customer task',
    category: 'support',
    icon: '✅',
    trigger: {
      type: 'activity_completed',
      conditions: [
        { field: 'activity_type', operator: 'equals', value: 'task' }
      ]
    },
    actions: [
      {
        type: 'send_email',
        config: {
          template: 'task_completed_follow_up',
          subject: 'We completed your request',
          from: 'support@company.com'
        }
      },
      {
        type: 'create_activity',
        config: {
          type: 'note',
          description: 'Follow-up sent after task completion'
        }
      }
    ]
  }
];

export const getTemplatesByCategory = (category: WorkflowTemplate['category']) => {
  return workflowTemplates.filter(t => t.category === category);
};

export const getPopularTemplates = () => {
  return workflowTemplates.filter(t => t.popular);
};

export const getTemplateById = (id: string) => {
  return workflowTemplates.find(t => t.id === id);
};
